import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import {
  Avatar,
  Button,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Typography,
} from '@mui/material';
import { Box, Stack } from '@mui/system';
import { DataGrid } from '@mui/x-data-grid';
import { ClearIcon } from '@mui/x-date-pickers';
import { post } from 'api';
import { get } from 'api';
import { url } from 'config/config';
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';
// import IconifyIcon from 'components/base/IconifyIcon';
import DataGridPagination from 'components/pagination/DataGridPagination';
import StyledTextField from 'components/styled/StyledTextField';

const Audit = () => {
  // const { sessionUser } = useAuth();

  const ViewButton = ({ row }) => {
    const navigate = useNavigate();

    const handleView = () => {
      navigate('/user-info', { state: row });
    };

    return (
      <IconButton onClick={handleView}>
        <RemoveRedEyeOutlinedIcon />
      </IconButton>
    );
  };
  const columns = useMemo(
    () => [
      {
        field: 'view',
        headerName: 'View',
        sortable: false,
        filterable: false,
        minWidth: 80,
        renderCell: (params) => <ViewButton row={params.row} />,
      },
      {
        field: 'userId',
        headerName: 'User',
        minWidth: 280,
        // flex: 1,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const {
            firstName = '',
            lastName = '',
            profileImage = '',
            empId = '',
          } = params.row.userId || {};

          return (
            <Stack
              sx={{
                gap: 1.5,
                alignItems: 'center',
              }}
            >
              <Avatar
                alt={firstName.toUpperCase()}
                src={profileImage ? `${url}${profileImage}` : ''}
                sx={{ width: 24, height: 24 }}
              />
              <div>
                <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
                  {`${firstName} ${lastName}`}
                </Typography>
                <Typography variant="caption">{`${empId}`}</Typography>
              </div>
            </Stack>
          );
        },
      },
      {
        field: 'tableName',
        headerName: 'Table',
        minWidth: 150,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          return (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
                {params.row.tableName}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: '_id',
        headerName: 'Entity ID',
        minWidth: 250,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          return (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
                {params.row._id}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'action',
        headerName: 'Action',
        minWidth: 150,
        renderCell: (params) => <Typography variant="subtitle2">{params.row.action}</Typography>,
      },
      {
        field: 'createdAt',
        headerName: 'Timestamp',
        minWidth: 200,
        renderCell: (params) => (
          <Typography variant="subtitle2">
            {moment(params.row.createdAt).format('DD/MM/YYYY HH:mm')}
          </Typography>
        ),
      },
    ],
    [],
  );
  const [search, setSearch] = useState('');
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
    search: search,
  });
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [actionFilter, setActionFilter] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [selectedTables, setSelectedTables] = useState([]);

  const fetchUser = async () => {
    try {
      const res = await get('/users/dropdown_user');

      if (res.status == 200) {
        setUsers(res.data);
      } else {
        enqueueSnackbar(res.message || 'Error', { variant: 'error', autoHideDuration: 3000 });
        setUsers([]);
      }
    } catch (error) {
      enqueueSnackbar(error.message || 'Error', { variant: 'error', autoHideDuration: 3000 });
      setUsers([]);
    }
  };
  useEffect(() => {
    fetchUser();
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPaginationModel((prev) => ({ ...prev, search: search }));
    }, 600);
    return () => clearTimeout(timeout);
  }, [search]);
  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await post('admin/audit', {
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
        search: paginationModel.search,
        action: actionFilter,
        userId: selectedUser,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        tableNames: selectedTables.length ? selectedTables : undefined,
      });
      if (res.status == 200) {
        setData(res.data.data);
        setTotal(res.data.total);
      } else {
        enqueueSnackbar(res.message || 'Error', { variant: 'error', autoHideDuration: 3000 });
        setData([]);
        setTotal(0);
      }
    } catch (error) {
      enqueueSnackbar(error.message || 'Error', { variant: 'error', autoHideDuration: 3000 });
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [paginationModel]);
  useEffect(() => {
    if (dateRange.startDate || dateRange.endDate) {
      fetchData();
    }
  }, [dateRange]);

  const TABLE_OPTIONS = [
    'GedaManagement',
    'InstallationManagement',
    'OtherManagement',
    'addInventory',
    'attendance',
    'customers',
    'dispatch',
    'document',
    'enquiry',
    'followup',
    'payment',
    'product',
    'productType',
    'users',
    'LeaveRequest',
  ];

  return (
    <>
      <Paper sx={{ flex: 1, p: { xs: 3, md: 5 } }}>
        {/* Page Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" fontWeight={600}>
            Audit Logs
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Monitor all changes across system modules.
          </Typography>
        </Box>

        {/* Filters */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 4 }}>
          <StyledTextField
            select
            label="Select Tables"
            SelectProps={{
              multiple: true,
              IconComponent: selectedTables.length > 0 ? () => null : KeyboardArrowDownIcon,
              renderValue: (selected) => {
                if (selected.length === 0) return 'All Tables';

                if (selected.length <= 2) {
                  return selected.join(', ');
                }

                return `${selected.slice(0, 2).join(', ')} +${selected.length - 2} more`;
              },
            }}
            value={selectedTables}
            onChange={(e) => {
              setSelectedTables(e.target.value);
              setPaginationModel((prev) => ({ ...prev, page: 0 }));
            }}
            helperText="Leave empty to show all tables"
            slotProps={{
              input: {
                endAdornment:
                  selectedTables.length > 0 ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelectedTables([]);
                          setPaginationModel((prev) => ({ ...prev, page: 0 }));
                        }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
              },
            }}
          >
            {' '}
            {TABLE_OPTIONS.map((table) => (
              <MenuItem key={table} value={table}>
                {table}
              </MenuItem>
            ))}
          </StyledTextField>

          {/* Action Dropdown */}
          <StyledTextField
            select
            label="Action"
            value={actionFilter}
            onChange={(e) => {
              setActionFilter(e.target.value);
              setPaginationModel((prev) => ({ ...prev, page: 0 }));
            }}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="CREATE">Create</MenuItem>
            <MenuItem value="UPDATE">Update</MenuItem>
            <MenuItem value="DELETE">Delete</MenuItem>
          </StyledTextField>

          {/* Multi User Select */}
          <StyledTextField
            select
            label="Select Users"
            value={selectedUser}
            onChange={(e) => {
              setSelectedUser(e.target.value);
              setPaginationModel((prev) => ({ ...prev, page: 0 }));
            }}
            sx={{ minWidth: 220 }}
          >
            {users?.map((user) => (
              <MenuItem key={user._id} value={user._id}>
                {user.firstName} {user.lastName}
              </MenuItem>
            ))}
          </StyledTextField>

          {/* Date Range */}
          <StyledTextField
            type="date"
            label="Start Date"
            InputLabelProps={{ shrink: true }}
            onChange={(e) =>
              setDateRange((prev) => ({
                ...prev,
                startDate: e.target.value,
              }))
            }
          />

          <StyledTextField
            type="date"
            label="End Date"
            InputLabelProps={{ shrink: true }}
            onChange={(e) =>
              setDateRange((prev) => ({
                ...prev,
                endDate: e.target.value,
              }))
            }
          />
          <Button
            variant="soft"
            shape="square"
            color="neutral"
            sx={{ p: 2, mt: 2.4 }}
            onClick={() => {
              setSelectedTables([]);
              setSelectedUser(null);
              setActionFilter('');
              setDateRange({ startDate: null, endDate: null });
              setSearch('');
              setPaginationModel({ page: 0, pageSize: 10, search: '' });
            }}
          >
            Reset
          </Button>
        </Stack>

        {/* <StyledTextField
          id="search-box"
          type="search"
          fullWidth
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search User Name"
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <IconifyIcon icon="material-symbols:search-rounded" fontSize={20} />
                </InputAdornment>
              ),
            },
          }}
          sx={{
            maxWidth: { sm: 200, md: 240 },
            flexGrow: { xs: 1, sm: 0 },
            mb: 3,
          }}
        /> */}
        <Box sx={{ width: '100%' }}>
          <DataGrid
            loading={loading}
            rows={data}
            columns={columns}
            getRowId={(row) => row._id}
            pagination
            paginationMode="server"
            rowCount={total}
            paginationModel={paginationModel} // { page: 0, pageSize: 10 }
            onPaginationModelChange={(e) =>
              setPaginationModel(() => ({
                page: e.page,
                pageSize: e.pageSize,
                search,
              }))
            }
            pageSizeOptions={[5, 10, 20]}
            slots={{
              basePagination: (props) => <DataGridPagination showFullPagination {...props} />,
            }}
          />
        </Box>
      </Paper>
    </>
  );
};

export default Audit;
